import { useMemo, useState, useEffect, useRef, RefObject, useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MultiSelectDropdown, MultiSelectOption } from '@/components/ui/MultiSelectDropdown';
import {
  ChevronDownIcon, CheckIcon, MagnifyingGlassIcon, XMarkIcon, HeartIcon,
  BookmarkIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid, StarIcon as StarSolid, BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';
import { ExportDropdown } from '@/components/export/ExportDropdown';
import { savedViewApi, viewApi, type SavedFilterState, type SavedView } from '@/services/api';
import { SaveViewModal } from '@/components/modals/SaveViewModal';
import { useAuth } from '@/contexts/AuthContext';
import type { FilterSummary } from '@/utils/exportView';

// A collapsible section inside the "More" panel. The header shows the filter name (so users can
// see which filters are available) plus a count badge; entries are revealed only when expanded.
// Defaults to collapsed so the panel stays a compact, scannable menu of filters.
function AccordionSection({
  label,
  count,
  defaultOpen = false,
  children,
}: {
  label: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm hover:bg-surface-hover"
      >
        <span className="font-medium text-text-primary flex items-center gap-2">
          {label}
          {count !== undefined && count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
              {count}
            </span>
          )}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-text-tertiary transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-2 pb-2">{children}</div>}
    </div>
  );
}

// Renders just the checkbox option rows (no header) with an inner scroll for long lists.
function CheckboxOptionList({
  options,
  selected,
  onToggle,
  emptyText = 'No options available',
}: {
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (value: string) => void;
  emptyText?: string;
}) {
  return (
    <div className="max-h-56 overflow-auto">
      {options.map(option => {
        const isSelected = selected.includes(option.value);
        return (
          <button
            key={option.value}
            onClick={() => onToggle(option.value)}
            disabled={option.disabled}
            className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm text-left rounded ${
              option.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-surface-hover'
            }`}
          >
            <div
              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-blue-600 border-blue-600' : 'border-border-default'
              }`}
            >
              {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
            </div>
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
      {options.length === 0 && (
        <div className="px-2 py-2 text-sm text-text-secondary">{emptyText}</div>
      )}
    </div>
  );
}

interface FilterState {
  quarters: string[];
  products: string[];
  subProducts: string[];
  statuses: string[];
  owners: string[];
  classifications: string[];
  tags: string[];
  hasDependencies: boolean;
  isStarred: boolean;
  // Scoring-only filters (only applied when showScoreFilters is set)
  impactValues: number[];
  effortValues: number[];
  minVotes: number;
}

interface FilterBarProps {
  onFilteredInitiativesChange: (initiatives: any[]) => void;
  viewType: 'timeline' | 'list' | 'kanban' | 'calendar';
  viewContainerRef: RefObject<HTMLDivElement>;
  customCount?: number;
  customCountLabel?: string;
  hideHasDependencies?: boolean;
  savedViewKey?: 'timeline' | 'list' | 'kanban' | 'calendar' | 'scoring' | 'audit' | 'comments' | 'dependency';
  /** Hide the Export dropdown (e.g. views that don't support canvas export yet, like Kanban) */
  hideExport?: boolean;
  /** Current timeframe mode — only 'weekly' changes the count display (adds the dated-records subline) */
  viewMode?: 'weekly' | 'monthly' | 'quarterly';
  /** In weekly mode, how many of the found records are date-mode (have explicit start/end dates) */
  weeklyDatedCount?: number;
  /** Per-initiative score data, keyed by initiative id — required for the Impact/Effort/Votes filters */
  scoreMap?: Record<string, { impact: number | null; effort: number | null; voteCount: number }>;
  /** Show the Impact/Effort/Votes filters (Scoring page only) */
  showScoreFilters?: boolean;
  /** Filter state injected from a selected named view — applied at mount (highest priority). */
  injectedFilterState?: SavedFilterState | null;
  /** Extra fields (e.g. Kanban's pending column layout) folded into whatever filterState is
   * next saved via Update or Save As — merged on top of the live filter selections. When set
   * (and no named view is active) a "Save As…" hint appears next to the view-actions icon,
   * since that change can't be saved silently onto the default view. */
  pendingExtraFilterState?: Partial<SavedFilterState>;
}

// localStorage helpers
function readLsState(key: string): SavedFilterState | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLsState(key: string, state: SavedFilterState) {
  localStorage.setItem(key, JSON.stringify(state));
}

export function FilterBar({ onFilteredInitiativesChange, viewType, viewContainerRef, customCount, customCountLabel, hideHasDependencies, savedViewKey, hideExport, viewMode, weeklyDatedCount, scoreMap, showScoreFilters, injectedFilterState, pendingExtraFilterState }: FilterBarProps) {
  const {
    initiatives,
    products,
    quarters,
    statuses,
    classifications,
    tags,
    owners,
    activeSavedView,
    setActiveSavedView,
    loadAccessibleViews,
    notify,
  } = useAppStore();
  const fc = useFieldConfig();
  const labs = useLabsFeatures();
  const { user, updateUser } = useAuth();

  // Responsive breakpoints — collapse lower-priority filters into More as the window narrows
  //   ≥1536 (2xl): Product, SubProduct, Owner all inline
  //   1280–1535 (xl): Product inline; SubProduct + Owner collapse into More
  //   640–1279: Product also collapses into More (only Search, Months, Status stay inline)
  //   <640 (phone): Months and Status collapse too — only Search + More + action icons stay inline.
  //   Below `isNarrow`, MultiSelectDropdown's trigger buttons (min-w-0 + whitespace-nowrap, no
  //   truncation) shrink below their text's width and the label visibly spills onto neighboring
  //   buttons — collapsing them into the existing More panel avoids that rather than patching the
  //   shared dropdown component's overflow behavior.
  const isWide = useMediaQuery('(min-width: 1536px)');
  const isMedium = useMediaQuery('(min-width: 1280px)');
  const isNarrow = useMediaQuery('(min-width: 640px)');
  const productInline = isMedium;
  const subProductInline = isWide;
  const ownerInline = isWide;
  const scoringInline = isWide;
  const monthsInline = isNarrow;
  const statusInline = isNarrow;

  // Saved view state
  const lsKey = savedViewKey && user?.id ? `cpr_saved_view_${savedViewKey}_${user.id}` : null;
  const [savedState, setSavedState] = useState<SavedFilterState | null>(() =>
    lsKey ? readLsState(lsKey) : null
  );
  const [heartTooltip, setHeartTooltip] = useState('');
  const hadNoLsOnMount = useRef(lsKey ? readLsState(lsKey) === null : true);

  // Lazy initialize: an injected named-view filter state wins (mount-time; page remounts on view
  // switch via the `key` prop), otherwise fall back to localStorage so first render already has
  // saved filters (no flash).
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    if (injectedFilterState) return injectedFilterState.searchQuery ?? '';
    return lsKey ? (readLsState(lsKey)?.searchQuery ?? '') : '';
  });

  const [filters, setFilters] = useState<FilterState>(() => {
    const saved = injectedFilterState ?? (lsKey ? readLsState(lsKey) : null);
    return {
      quarters: saved?.quarters ?? [],
      products: saved?.products ?? [],
      subProducts: saved?.subProducts ?? [],
      statuses: saved?.statuses ?? [],
      owners: saved?.owners ?? [],
      classifications: saved?.classifications ?? [],
      tags: saved?.tags ?? [],
      hasDependencies: saved?.hasDependencies ?? false,
      isStarred: saved?.isStarred ?? false,
      impactValues: saved?.impactValues ?? [],
      effortValues: saved?.effortValues ?? [],
      minVotes: saved?.minVotes ?? 0,
    };
  });

  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [votesMenuOpen, setVotesMenuOpen] = useState(false);
  const votesMenuRef = useRef<HTMLDivElement>(null);

  // Named saved views (Save icon / action menu)
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveMode, setSaveMode] = useState<'create' | 'edit' | 'saveas'>('create');
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  const [saveTooltip, setSaveTooltip] = useState('');

  // Close more menu / votes menu / view menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
      if (votesMenuRef.current && !votesMenuRef.current.contains(event.target as Node)) {
        setVotesMenuOpen(false);
      }
      if (viewMenuRef.current && !viewMenuRef.current.contains(event.target as Node)) {
        setViewMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // On mount: sync saved view from server, apply if localStorage was empty
  useEffect(() => {
    if (!savedViewKey || !lsKey) return;
    savedViewApi.get(savedViewKey).then(({ filterState }) => {
      if (!filterState) {
        // Server says there's no saved default (e.g. cleared from another device, or never
        // really saved) — the local cache must not keep claiming otherwise.
        localStorage.removeItem(lsKey);
        setSavedState(null);
        return;
      }
      writeLsState(lsKey, filterState);
      setSavedState(filterState);
      // Auto-apply if the user had no locally cached state on this device
      if (hadNoLsOnMount.current) {
        hadNoLsOnMount.current = false;
        setSearchQuery(filterState.searchQuery || '');
        setFilters({
          quarters: filterState.quarters || [],
          products: filterState.products || [],
          subProducts: filterState.subProducts || [],
          statuses: filterState.statuses || [],
          owners: filterState.owners || [],
          classifications: filterState.classifications || [],
          tags: filterState.tags || [],
          hasDependencies: filterState.hasDependencies || false,
          isStarred: filterState.isStarred || false,
          impactValues: filterState.impactValues ?? [],
          effortValues: filterState.effortValues ?? [],
          minVotes: filterState.minVotes ?? 0,
        });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedViewKey]);

  // Get all sub-products from products
  const allSubProducts = useMemo(() => {
    return products.flatMap(p => p.subProducts || []);
  }, [products]);

  // Compute which quarters have initiatives
  const quartersWithInitiatives = useMemo(() => {
    const quarterKeys = new Set<string>();
    initiatives.forEach(init => {
      if (init.startQuarter) quarterKeys.add(init.startQuarter);
      if (init.endQuarter) quarterKeys.add(init.endQuarter);
    });
    return quarterKeys;
  }, [initiatives]);

  // Compute which products have initiatives
  const productsWithInitiatives = useMemo(() => {
    const productIds = new Set<string>();
    initiatives.forEach(init => {
      const subProduct = allSubProducts.find(sp => sp.id === init.subProductId);
      if (subProduct) {
        productIds.add(subProduct.productId);
      }
    });
    return productIds;
  }, [initiatives, allSubProducts]);

  // Compute which sub-products have initiatives (filtered by selected products)
  const subProductsWithInitiatives = useMemo(() => {
    const subProductIds = new Set<string>();
    initiatives.forEach(init => {
      if (init.subProductId) subProductIds.add(init.subProductId);
    });
    return subProductIds;
  }, [initiatives]);

  // Compute which statuses are used
  const statusesInUse = useMemo(() => {
    const statusIds = new Set<string>();
    initiatives.forEach(init => {
      if (init.statusId) statusIds.add(init.statusId);
    });
    return statusIds;
  }, [initiatives]);

  // Compute which classifications are used
  const classificationsInUse = useMemo(() => {
    const classificationIds = new Set<string>();
    initiatives.forEach(init => {
      if (init.classificationId) classificationIds.add(init.classificationId);
    });
    return classificationIds;
  }, [initiatives]);

  // Compute which tags are used
  const tagsInUse = useMemo(() => {
    const tagIds = new Set<string>();
    initiatives.forEach(init => {
      init.tags?.forEach((t: any) => {
        if (t.tagId) tagIds.add(t.tagId);
      });
    });
    return tagIds;
  }, [initiatives]);

  // Compute which owners have initiatives
  const ownersInUse = useMemo(() => {
    const ownerIds = new Set<string>();
    initiatives.forEach(init => {
      if (init.userId) ownerIds.add(init.userId);
    });
    return ownerIds;
  }, [initiatives]);


  // Apply filters to get filtered initiatives
  const filteredInitiatives = useMemo(() => {
    let result = [...initiatives];

    // Filter by search query (full-text search across multiple fields)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(init => {
        // Search in title
        if (init.title?.toLowerCase().includes(query)) return true;
        // Search in notes
        if (init.notes?.toLowerCase().includes(query)) return true;
        // Search in user/owner name
        if (init.user?.name?.toLowerCase().includes(query)) return true;
        // Search in status name
        if (init.status?.name?.toLowerCase().includes(query)) return true;
        // Search in classification name
        if (init.classification?.name?.toLowerCase().includes(query)) return true;
        // Search in tag names
        if (init.tags?.some((t: any) => t.tag?.name?.toLowerCase().includes(query))) return true;
        // Search in sub-product name
        if (init.subProduct?.name?.toLowerCase().includes(query)) return true;
        // Search in product name (via subProduct)
        if (init.subProduct?.product?.name?.toLowerCase().includes(query)) return true;
        return false;
      });
    }

    // Filter by quarters
    if (filters.quarters.length > 0) {
      result = result.filter(init => {
        // Initiative matches if its start or end quarter is in the selected quarters
        // Or if it spans across selected quarters
        const startIdx = quarters.findIndex(q => q.key === init.startQuarter);
        const endIdx = init.endQuarter
          ? quarters.findIndex(q => q.key === init.endQuarter)
          : startIdx;

        return filters.quarters.some(qKey => {
          const qIdx = quarters.findIndex(q => q.key === qKey);
          return qIdx >= startIdx && qIdx <= endIdx;
        });
      });
    }

    // Filter by products
    if (filters.products.length > 0) {
      result = result.filter(init => {
        const subProduct = allSubProducts.find(sp => sp.id === init.subProductId);
        return subProduct && filters.products.includes(subProduct.productId);
      });
    }

    // Filter by sub-products
    if (filters.subProducts.length > 0) {
      result = result.filter(init => filters.subProducts.includes(init.subProductId));
    }

    // Filter by statuses
    if (filters.statuses.length > 0) {
      result = result.filter(init => filters.statuses.includes(init.statusId));
    }

    // Filter by classifications
    if (filters.classifications.length > 0) {
      result = result.filter(init => init.classificationId && filters.classifications.includes(init.classificationId));
    }

    // Filter by tags
    if (filters.tags.length > 0) {
      result = result.filter(init =>
        init.tags?.some((t: any) => filters.tags.includes(t.tagId))
      );
    }

    // Filter by owners
    if (filters.owners.length > 0) {
      result = result.filter(init => init.userId && filters.owners.includes(init.userId));
    }

    // Filter by has dependencies
    if (filters.hasDependencies) {
      result = result.filter(init => init.dependencies && init.dependencies.length > 0);
    }

    // Filter by starred
    if (filters.isStarred) {
      result = result.filter(init => init.isStarred === true);
    }

    // Scoring-only filters (Impact / Effort / minimum Votes) via scoreMap
    if (showScoreFilters && scoreMap) {
      if (filters.impactValues.length > 0) {
        result = result.filter(init => {
          const v = scoreMap[init.id]?.impact;
          return v != null && filters.impactValues.includes(v);
        });
      }
      if (filters.effortValues.length > 0) {
        result = result.filter(init => {
          const v = scoreMap[init.id]?.effort;
          return v != null && filters.effortValues.includes(v);
        });
      }
      if (filters.minVotes > 0) {
        result = result.filter(init => (scoreMap[init.id]?.voteCount ?? 0) >= filters.minVotes);
      }
    }

    return result;
  }, [initiatives, searchQuery, filters, quarters, allSubProducts, scoreMap, showScoreFilters]);

  // Notify parent of filtered initiatives
  // Note: onFilteredInitiativesChange is a stable setState function, so we exclude it from deps
  useEffect(() => {
    onFilteredInitiativesChange(filteredInitiatives);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInitiatives]);

  // Compute grey-out logic for each filter option
  const getQuarterOptions = (): MultiSelectOption[] => {
    return quarters.map(q => {
      const hasInitiatives = quartersWithInitiatives.has(q.key);
      return {
        value: q.key,
        label: q.fullLabel,
        disabled: !hasInitiatives,
      };
    });
  };

  const getProductOptions = (): MultiSelectOption[] => {
    return products.map(p => {
      const hasInitiatives = productsWithInitiatives.has(p.id);
      return {
        value: p.id,
        label: p.name,
        disabled: !hasInitiatives,
      };
    });
  };

  const getSubProductOptions = (): MultiSelectOption[] => {
    // If products are selected, only show sub-products from those products
    let availableSubProducts = allSubProducts;
    if (filters.products.length > 0) {
      availableSubProducts = allSubProducts.filter(sp =>
        filters.products.includes(sp.productId)
      );
    }

    return availableSubProducts.map(sp => {
      const hasInitiatives = subProductsWithInitiatives.has(sp.id);
      return {
        value: sp.id,
        label: sp.name,
        disabled: !hasInitiatives,
      };
    });
  };

  const getStatusOptions = (): MultiSelectOption[] => {
    return statuses.map(s => ({
      value: s.id,
      label: s.name,
      disabled: !statusesInUse.has(s.id),
    }));
  };

  const getClassificationOptions = (): MultiSelectOption[] => {
    return classifications.map(c => ({
      value: c.id,
      label: c.name,
      disabled: !classificationsInUse.has(c.id),
    }));
  };

  const getTagOptions = (): MultiSelectOption[] => {
    return tags.map(t => ({
      value: t.id,
      label: t.name,
      disabled: !tagsInUse.has(t.id),
    }));
  };

  const getOwnerOptions = (): MultiSelectOption[] => {
    return (owners || []).map(o => ({
      value: o.id,
      label: o.name,
      disabled: !ownersInUse.has(o.id),
    }));
  };

  // Update filter and handle product->subProduct dependency
  const handleProductChange = (selectedProducts: string[]) => {
    setFilters(prev => {
      // If products changed, filter out sub-products that are no longer valid
      const validSubProducts = prev.subProducts.filter(spId => {
        if (selectedProducts.length === 0) return true;
        const sp = allSubProducts.find(s => s.id === spId);
        return sp && selectedProducts.includes(sp.productId);
      });

      return {
        ...prev,
        products: selectedProducts,
        subProducts: validSubProducts,
      };
    });
  };

  const clearAllFilters = () => {
    // If deviated from a saved favourite, restore it ("Reset" behaviour)
    const stateToRestore = savedState ?? (lsKey ? readLsState(lsKey) : null);
    if (stateToRestore) {
      if (!savedState) setSavedState(stateToRestore);
      setSearchQuery(stateToRestore.searchQuery || '');
      setFilters({
        quarters: stateToRestore.quarters ?? [],
        products: stateToRestore.products ?? [],
        subProducts: stateToRestore.subProducts ?? [],
        statuses: stateToRestore.statuses ?? [],
        owners: stateToRestore.owners ?? [],
        classifications: stateToRestore.classifications ?? [],
        tags: stateToRestore.tags ?? [],
        hasDependencies: stateToRestore.hasDependencies ?? false,
        isStarred: stateToRestore.isStarred ?? false,
        impactValues: stateToRestore.impactValues ?? [],
        effortValues: stateToRestore.effortValues ?? [],
        minVotes: stateToRestore.minVotes ?? 0,
      });
      return;
    }
    // No saved view → clear everything
    setSearchQuery('');
    setFilters({
      quarters: [],
      products: [],
      subProducts: [],
      statuses: [],
      owners: [],
      classifications: [],
      tags: [],
      hasDependencies: false,
      isStarred: false,
      impactValues: [],
      effortValues: [],
      minVotes: 0,
    });
  };

  const hasActiveFilters = searchQuery.trim() !== '' ||
    filters.quarters.length > 0 ||
    filters.products.length > 0 ||
    filters.subProducts.length > 0 ||
    filters.statuses.length > 0 ||
    filters.owners.length > 0 ||
    filters.classifications.length > 0 ||
    filters.tags.length > 0 ||
    filters.hasDependencies ||
    filters.isStarred ||
    filters.impactValues.length > 0 ||
    filters.effortValues.length > 0 ||
    filters.minVotes > 0;

  // Check if More filters have selections — includes overflow counts for items not inline at the current breakpoint
  const moreFiltersCount =
    (fc.isVisible('classification') ? filters.classifications.length : 0) +
    (fc.isVisible('tags') ? filters.tags.length : 0) +
    (!hideHasDependencies && labs.isEnabled('dependencies') && filters.hasDependencies ? 1 : 0) +
    (filters.isStarred ? 1 : 0) +
    (!monthsInline ? filters.quarters.length : 0) +
    (!statusInline ? filters.statuses.length : 0) +
    (!productInline ? filters.products.length : 0) +
    (!subProductInline ? filters.subProducts.length : 0) +
    (!ownerInline ? filters.owners.length : 0) +
    (showScoreFilters && !scoringInline
      ? filters.impactValues.length + filters.effortValues.length + (filters.minVotes > 0 ? 1 : 0)
      : 0);

  // Build current filter state snapshot
  const currentFilterState = useCallback((): SavedFilterState => ({
    searchQuery: searchQuery.trim(),
    quarters: filters.quarters,
    products: filters.products,
    subProducts: filters.subProducts,
    statuses: filters.statuses,
    owners: filters.owners,
    classifications: filters.classifications,
    tags: filters.tags,
    hasDependencies: filters.hasDependencies,
    isStarred: filters.isStarred,
    impactValues: filters.impactValues,
    effortValues: filters.effortValues,
    minVotes: filters.minVotes,
    // Kanban column layout lives in this same blob (see KanbanPage.tsx) but isn't a filter
    // field this component manages — carry it forward so saving filters doesn't wipe it out.
    ...(savedState?.kanbanColumns ? { kanbanColumns: savedState.kanbanColumns } : {}),
    // A pending column-layout change (not yet saved anywhere) always wins over whatever was
    // previously saved — see the requestSaveAsToken effect below.
    ...(pendingExtraFilterState ?? {}),
  }), [searchQuery, filters, savedState, pendingExtraFilterState]);

  // Order-independent comparison so heart stays red regardless of multi-select order
  const isCurrentStateSaved = useCallback((): boolean => {
    if (!savedState) return false;
    const cur = currentFilterState();
    return (
      cur.searchQuery === savedState.searchQuery &&
      JSON.stringify(cur.quarters.slice().sort()) === JSON.stringify(savedState.quarters.slice().sort()) &&
      JSON.stringify(cur.products.slice().sort()) === JSON.stringify(savedState.products.slice().sort()) &&
      JSON.stringify(cur.subProducts.slice().sort()) === JSON.stringify(savedState.subProducts.slice().sort()) &&
      JSON.stringify(cur.statuses.slice().sort()) === JSON.stringify(savedState.statuses.slice().sort()) &&
      JSON.stringify(cur.owners.slice().sort()) === JSON.stringify(savedState.owners.slice().sort()) &&
      JSON.stringify(cur.classifications.slice().sort()) === JSON.stringify(savedState.classifications.slice().sort()) &&
      JSON.stringify(cur.tags.slice().sort()) === JSON.stringify(savedState.tags.slice().sort()) &&
      cur.hasDependencies === savedState.hasDependencies &&
      cur.isStarred === (savedState.isStarred ?? false) &&
      JSON.stringify((cur.impactValues ?? []).slice().sort()) === JSON.stringify((savedState.impactValues ?? []).slice().sort()) &&
      JSON.stringify((cur.effortValues ?? []).slice().sort()) === JSON.stringify((savedState.effortValues ?? []).slice().sort()) &&
      (cur.minVotes ?? 0) === (savedState.minVotes ?? 0)
    );
  }, [savedState, currentFilterState]);

  const heartActive = savedViewKey ? isCurrentStateSaved() : false;

  const handleHeartClick = () => {
    if (!savedViewKey || !lsKey) return;

    if (heartActive) {
      // Current filters exactly match saved → remove the saved view
      savedViewApi.clear(savedViewKey).catch(() => {});
      localStorage.removeItem(lsKey);
      setSavedState(null);
      return;
    }

    // A saved view exists but user has deviated → RESTORE it
    const stateToRestore = savedState ?? readLsState(lsKey);
    if (stateToRestore) {
      if (!savedState) setSavedState(stateToRestore);
      setSearchQuery(stateToRestore.searchQuery || '');
      setFilters({
        quarters: stateToRestore.quarters ?? [],
        products: stateToRestore.products ?? [],
        subProducts: stateToRestore.subProducts ?? [],
        statuses: stateToRestore.statuses ?? [],
        owners: stateToRestore.owners ?? [],
        classifications: stateToRestore.classifications ?? [],
        tags: stateToRestore.tags ?? [],
        hasDependencies: stateToRestore.hasDependencies ?? false,
        isStarred: stateToRestore.isStarred ?? false,
        impactValues: stateToRestore.impactValues ?? [],
        effortValues: stateToRestore.effortValues ?? [],
        minVotes: stateToRestore.minVotes ?? 0,
      });
      return;
    }

    // No saved view at all
    if (!hasActiveFilters) {
      setHeartTooltip('You cannot favourite the default view');
      setTimeout(() => setHeartTooltip(''), 3000);
      return;
    }

    // Save current state as default
    const state = currentFilterState();
    savedViewApi.save(savedViewKey, state).catch(() => {});
    localStorage.setItem(lsKey, JSON.stringify(state));
    setSavedState(state);
    // One default per view type — favoriting the Default view unpins any named view for it,
    // so this raw-filter default becomes the loading default.
    const favField =
      savedViewKey === 'timeline' ? 'favoriteTimelineViewId'
      : savedViewKey === 'list' ? 'favoriteListViewId'
      : savedViewKey === 'kanban' ? 'favoriteKanbanViewId'
      : savedViewKey === 'calendar' ? 'favoriteCalendarViewId'
      : null;
    if (favField && user?.[favField as keyof typeof user]) {
      updateUser({ [favField]: null });
      setActiveSavedView(null);
      loadAccessibleViews();
    }
  };

  const heartTitle = heartActive
    ? 'Remove saved default view'
    : (savedState ?? (lsKey ? readLsState(lsKey) : null))
      ? 'Restore saved default view'
      : 'Save as default view';

  // ── Named saved views (Save icon / action menu) ────────────────────────────
  const canCreateViews = user?.role === 'ADMIN' || user?.role === 'EDITOR';
  // The named, shareable views (switcher / sharing / Save As / view pinning) live on the
  // Timeline, List, Kanban & Calendar surfaces — identified by savedViewKey, NOT viewType
  // (report pages like Dependency/Audit/Comments/Scoring reuse viewType="list" purely for
  // filter/export styling).
  const isNamedSurface = savedViewKey === 'timeline' || savedViewKey === 'list' || savedViewKey === 'kanban' || savedViewKey === 'calendar';
  // The bookmark menu replaces the plain heart button once the Saved Views lab is enabled —
  // it appears on any FilterBar that supports a personal default (i.e. anything with a
  // savedViewKey). When the lab is off, the original heart-only behavior above still applies.
  const savedViewsOn = labs.isEnabled('savedViews') && Boolean(savedViewKey);
  const namedViewsOn = savedViewsOn && isNamedSurface;
  // The active named view only applies on its own named surface
  const activeView: SavedView | null =
    namedViewsOn && activeSavedView && activeSavedView.viewType === savedViewKey
      ? activeSavedView
      : null;

  const handleViewSaved = useCallback(
    (view: SavedView) => {
      loadAccessibleViews();
      setActiveSavedView(view);
    },
    [loadAccessibleViews, setActiveSavedView],
  );

  // Toggle the active view as this user's pinned favorite (auto-loads on login).
  // Strictly per-user — never affects anyone else.
  const handleToggleFavorite = async () => {
    if (!activeView) return;
    setViewMenuOpen(false);
    const field =
      activeView.viewType === 'timeline' ? 'favoriteTimelineViewId'
      : activeView.viewType === 'list' ? 'favoriteListViewId'
      : activeView.viewType === 'kanban' ? 'favoriteKanbanViewId'
      : 'favoriteCalendarViewId';
    try {
      if (activeView.isFavorite) {
        await viewApi.unfavorite(activeView.id);
        updateUser({ [field]: null });
        setActiveSavedView({ ...activeView, isFavorite: false });
        notify('success', 'Removed from your default view');
      } else {
        const r = await viewApi.favorite(activeView.id);
        updateUser({ [field]: r.favoriteViewId });
        setActiveSavedView({ ...activeView, isFavorite: true });
        // One default per view type — pinning this view clears the Default view's raw-filter
        // default so its Favorite icon shows as unset.
        if (lsKey) localStorage.removeItem(lsKey);
        setSavedState(null);
        notify('success', 'Set as your default view');
      }
      loadAccessibleViews();
    } catch (e) {
      notify('error', (e as { message?: string })?.message || 'Failed to update favorite');
    }
  };

  // Whether the current filters differ from the active view's saved filters
  const isViewDirty = useMemo(() => {
    if (!activeView) return false;
    const cur = currentFilterState();
    const sv = activeView.filterState;
    const arrEq = (a?: (string | number)[], b?: (string | number)[]) =>
      JSON.stringify([...(a ?? [])].slice().sort()) === JSON.stringify([...(b ?? [])].slice().sort());
    const same =
      (cur.searchQuery || '') === (sv.searchQuery || '') &&
      arrEq(cur.quarters, sv.quarters) &&
      arrEq(cur.products, sv.products) &&
      arrEq(cur.subProducts, sv.subProducts) &&
      arrEq(cur.statuses, sv.statuses) &&
      arrEq(cur.owners, sv.owners) &&
      arrEq(cur.classifications, sv.classifications) &&
      arrEq(cur.tags, sv.tags) &&
      (cur.hasDependencies || false) === (sv.hasDependencies || false) &&
      (cur.isStarred || false) === (sv.isStarred || false) &&
      arrEq(cur.impactValues, sv.impactValues) &&
      arrEq(cur.effortValues, sv.effortValues) &&
      (cur.minVotes || 0) === (sv.minVotes || 0);
    return !same;
  }, [activeView, currentFilterState]);

  const handleUpdateView = async () => {
    if (!activeView || !isViewDirty) return;
    setViewMenuOpen(false);
    try {
      const updated = await viewApi.update(activeView.id, {
        filterState: currentFilterState(),
      });
      setActiveSavedView(updated);
      loadAccessibleViews();
      notify('success', 'View updated');
    } catch (e) {
      notify('error', (e as { message?: string })?.message || 'Failed to update view');
    }
  };

  const handleDeleteView = async () => {
    if (!activeView) return;
    setViewMenuOpen(false);
    if (!confirm(`Are you sure you want to delete the view "${activeView.title}"? This cannot be undone.`)) return;
    try {
      await viewApi.delete(activeView.id);
      setActiveSavedView(null); // page remounts → filters reset to default
      loadAccessibleViews();
      notify('success', 'View deleted');
    } catch (e) {
      notify('error', (e as { message?: string })?.message || 'Failed to delete view');
    }
  };

  // Open the wizard in a specific mode:
  //  - 'create'  → new view from current filters (bookmark icon, on the Default view)
  //  - 'saveas'  → new view from current filters (action menu, while inside a saved view)
  const openSaveModal = (mode: 'create' | 'saveas') => {
    // A pending column-layout change is itself a valid reason to prompt Save As, even with no
    // filters applied — bypass the "apply filters first" gate in that case.
    if (!hasActiveFilters && !pendingExtraFilterState) {
      setSaveTooltip('Apply filters first to save a view');
      setTimeout(() => setSaveTooltip(''), 3000);
      return;
    }
    setSaveMode(mode);
    setViewMenuOpen(false);
    setSaveModalOpen(true);
  };


  const openEditModal = () => {
    setSaveMode('edit');
    setViewMenuOpen(false);
    setSaveModalOpen(true);
  };

  // Build filter summary for export (with display names instead of IDs)
  const filterSummary: FilterSummary = useMemo(() => {
    return {
      search: searchQuery.trim() || undefined,
      quarters: filters.quarters.length > 0
        ? filters.quarters.map(key => quarters.find(q => q.key === key)?.fullLabel || key)
        : undefined,
      products: filters.products.length > 0
        ? filters.products.map(id => products.find(p => p.id === id)?.name || id)
        : undefined,
      subProducts: filters.subProducts.length > 0
        ? filters.subProducts.map(id => allSubProducts.find(sp => sp.id === id)?.name || id)
        : undefined,
      statuses: filters.statuses.length > 0
        ? filters.statuses.map(id => statuses.find(s => s.id === id)?.name || id)
        : undefined,
      classifications: filters.classifications.length > 0
        ? filters.classifications.map(id => classifications.find(c => c.id === id)?.name || id)
        : undefined,
      tags: filters.tags.length > 0
        ? filters.tags.map(id => tags.find(t => t.id === id)?.name || id)
        : undefined,
    };
  }, [searchQuery, filters, quarters, products, allSubProducts, statuses, classifications, tags, owners]);

  // "Reset" restores the current view's default: an active named view's saved filters when one
  // is selected, otherwise falls back to the existing raw-favorite/clear-everything behavior.
  const resetToViewDefault = () => {
    if (activeView) {
      const s = activeView.filterState;
      setSearchQuery(s.searchQuery || '');
      setFilters({
        quarters: s.quarters ?? [],
        products: s.products ?? [],
        subProducts: s.subProducts ?? [],
        statuses: s.statuses ?? [],
        owners: s.owners ?? [],
        classifications: s.classifications ?? [],
        tags: s.tags ?? [],
        hasDependencies: s.hasDependencies ?? false,
        isStarred: s.isStarred ?? false,
        impactValues: s.impactValues ?? [],
        effortValues: s.effortValues ?? [],
        minVotes: s.minVotes ?? 0,
      });
      return;
    }
    clearAllFilters();
  };
  const deviatedFromViewDefault = activeView ? isViewDirty : (hasActiveFilters && !heartActive);

  return (
    <>
    <div className="flex items-center justify-between gap-4">
      {/* Left side: Search and Filters */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Search Input - controlled input */}
        <div className="relative flex-shrink-0">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search initiatives..."
            className="w-28 sm:w-40 2xl:w-48 pl-9 pr-8 py-1.5 text-sm border border-border-default rounded-md bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-text-tertiary hover:text-text-secondary"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {monthsInline && (
          <MultiSelectDropdown
            label="Months"
            options={getQuarterOptions()}
            selected={filters.quarters}
            onChange={(selected) => setFilters(prev => ({ ...prev, quarters: selected }))}
          />
        )}

        {productInline && (
          <MultiSelectDropdown
            label={fc.label('product')}
            options={getProductOptions()}
            selected={filters.products}
            onChange={handleProductChange}
          />
        )}

        {subProductInline && (
          <MultiSelectDropdown
            label={fc.label('subProduct')}
            options={getSubProductOptions()}
            selected={filters.subProducts}
            onChange={(selected) => setFilters(prev => ({ ...prev, subProducts: selected }))}
          />
        )}

        {statusInline && (
          <MultiSelectDropdown
            label={fc.label('status')}
            options={getStatusOptions()}
            selected={filters.statuses}
            onChange={(selected) => setFilters(prev => ({ ...prev, statuses: selected }))}
          />
        )}

        {ownerInline && (
          <MultiSelectDropdown
            label={fc.label('owner')}
            options={getOwnerOptions()}
            selected={filters.owners}
            onChange={(selected) => setFilters(prev => ({ ...prev, owners: selected }))}
          />
        )}

        {/* Scoring-only inline filters (Impact / Effort / Votes) */}
        {showScoreFilters && scoringInline && (
          <>
            <MultiSelectDropdown
              label="Impact"
              options={[1, 2, 3, 4, 5].map((v) => ({ value: String(v), label: String(v) }))}
              selected={filters.impactValues.map(String)}
              onChange={(selected) =>
                setFilters(prev => ({ ...prev, impactValues: selected.map(Number) }))
              }
            />
            <MultiSelectDropdown
              label="Effort"
              options={[1, 2, 3, 4, 5].map((v) => ({ value: String(v), label: String(v) }))}
              selected={filters.effortValues.map(String)}
              onChange={(selected) =>
                setFilters(prev => ({ ...prev, effortValues: selected.map(Number) }))
              }
            />

            {/* Votes — single min-threshold dropdown pill */}
            <div className="relative min-w-0" ref={votesMenuRef}>
              <button
                onClick={() => setVotesMenuOpen(!votesMenuOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors min-w-0 whitespace-nowrap ${
                  filters.minVotes > 0
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-background-primary border-border-default text-text-primary hover:border-border-focus'
                }`}
              >
                <span className="text-xs font-medium text-text-secondary uppercase flex-shrink-0">Votes:</span>
                <span className="font-medium truncate max-w-[10rem]">
                  {filters.minVotes > 0 ? `≥ ${filters.minVotes}` : 'Any'}
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform flex-shrink-0 ${votesMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {votesMenuOpen && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-background-elevated border border-border-default rounded-md shadow-lg z-50 max-h-64 overflow-auto">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                    const isSelected = filters.minVotes === n;
                    return (
                      <button
                        key={`votes-${n}`}
                        onClick={() => {
                          setFilters(prev => ({ ...prev, minVotes: n }));
                          setVotesMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left ${
                          isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-surface-hover'
                        }`}
                      >
                        <div
                          className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-border-default'
                          }`}
                        >
                          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span>{n === 0 ? 'Any number of votes' : `≥ ${n}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* More Filters Menu — always rendered; content varies by breakpoint */}
        <div className="relative flex-shrink-0" ref={moreMenuRef}>
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors ${
                moreFiltersCount > 0
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-background-primary border-border-default text-text-primary hover:border-border-focus'
              }`}
            >
              <span className="font-medium">
                More {moreFiltersCount > 0 && `(${moreFiltersCount})`}
              </span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {moreMenuOpen && (
              <div className="absolute top-full left-0 mt-1 w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100vh-7rem)] overflow-y-auto bg-background-elevated border border-border-default rounded-md shadow-lg z-50">
                {/* Filters that overflowed the inline row appear here as collapsible sections */}
                {!monthsInline && (
                  <AccordionSection label="Months" count={filters.quarters.length}>
                    <CheckboxOptionList
                      options={getQuarterOptions()}
                      selected={filters.quarters}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          quarters: prev.quarters.includes(value)
                            ? prev.quarters.filter(v => v !== value)
                            : [...prev.quarters, value],
                        }))
                      }
                    />
                  </AccordionSection>
                )}
                {!statusInline && (
                  <AccordionSection label={fc.label('status')} count={filters.statuses.length}>
                    <CheckboxOptionList
                      options={getStatusOptions()}
                      selected={filters.statuses}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          statuses: prev.statuses.includes(value)
                            ? prev.statuses.filter(v => v !== value)
                            : [...prev.statuses, value],
                        }))
                      }
                    />
                  </AccordionSection>
                )}
                {!productInline && (
                  <AccordionSection label={fc.label('product')} count={filters.products.length}>
                    <CheckboxOptionList
                      options={getProductOptions()}
                      selected={filters.products}
                      onToggle={(value) =>
                        handleProductChange(
                          filters.products.includes(value)
                            ? filters.products.filter(v => v !== value)
                            : [...filters.products, value]
                        )
                      }
                    />
                  </AccordionSection>
                )}
                {!subProductInline && (
                  <AccordionSection label={fc.label('subProduct')} count={filters.subProducts.length}>
                    <CheckboxOptionList
                      options={getSubProductOptions()}
                      selected={filters.subProducts}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          subProducts: prev.subProducts.includes(value)
                            ? prev.subProducts.filter(v => v !== value)
                            : [...prev.subProducts, value],
                        }))
                      }
                    />
                  </AccordionSection>
                )}
                {!ownerInline && (
                  <AccordionSection label={fc.label('owner')} count={filters.owners.length}>
                    <CheckboxOptionList
                      options={getOwnerOptions()}
                      selected={filters.owners}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          owners: prev.owners.includes(value)
                            ? prev.owners.filter(v => v !== value)
                            : [...prev.owners, value],
                        }))
                      }
                    />
                  </AccordionSection>
                )}

                {showScoreFilters && !scoringInline && (
                  <>
                    <AccordionSection label="Impact" count={filters.impactValues.length}>
                      <CheckboxOptionList
                        options={[1, 2, 3, 4, 5].map((v) => ({ value: String(v), label: String(v) }))}
                        selected={filters.impactValues.map(String)}
                        onToggle={(value) =>
                          setFilters(prev => {
                            const n = Number(value);
                            return {
                              ...prev,
                              impactValues: prev.impactValues.includes(n)
                                ? prev.impactValues.filter(v => v !== n)
                                : [...prev.impactValues, n],
                            };
                          })
                        }
                      />
                    </AccordionSection>
                    <AccordionSection label="Effort" count={filters.effortValues.length}>
                      <CheckboxOptionList
                        options={[1, 2, 3, 4, 5].map((v) => ({ value: String(v), label: String(v) }))}
                        selected={filters.effortValues.map(String)}
                        onToggle={(value) =>
                          setFilters(prev => {
                            const n = Number(value);
                            return {
                              ...prev,
                              effortValues: prev.effortValues.includes(n)
                                ? prev.effortValues.filter(v => v !== n)
                                : [...prev.effortValues, n],
                            };
                          })
                        }
                      />
                    </AccordionSection>
                    <AccordionSection label="Votes" count={filters.minVotes > 0 ? 1 : 0}>
                      <div className="max-h-56 overflow-auto">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                          const isSelected = filters.minVotes === n;
                          return (
                            <button
                              key={`more-votes-${n}`}
                              onClick={() => setFilters(prev => ({ ...prev, minVotes: n }))}
                              className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm text-left rounded ${
                                isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-surface-hover'
                              }`}
                            >
                              <div
                                className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-blue-600 border-blue-600' : 'border-border-default'
                                }`}
                              >
                                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                              </div>
                              <span>{n === 0 ? 'Any number of votes' : `≥ ${n}`}</span>
                            </button>
                          );
                        })}
                      </div>
                    </AccordionSection>
                  </>
                )}

                {fc.isVisible('classification') && (
                  <AccordionSection label={fc.label('classification')} count={filters.classifications.length}>
                    <CheckboxOptionList
                      options={getClassificationOptions()}
                      selected={filters.classifications}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          classifications: prev.classifications.includes(value)
                            ? prev.classifications.filter(v => v !== value)
                            : [...prev.classifications, value],
                        }))
                      }
                    />
                  </AccordionSection>
                )}

                {fc.isVisible('tags') && (
                  <AccordionSection label={fc.label('tags')} count={filters.tags.length}>
                    <CheckboxOptionList
                      options={getTagOptions()}
                      selected={filters.tags}
                      onToggle={(value) =>
                        setFilters(prev => ({
                          ...prev,
                          tags: prev.tags.includes(value)
                            ? prev.tags.filter(v => v !== value)
                            : [...prev.tags, value],
                        }))
                      }
                      emptyText="No tags in use"
                    />
                  </AccordionSection>
                )}

                {/* Toggles — not accordion sections, always visible at the bottom */}
                <div className="p-2 space-y-1">
                  {!hideHasDependencies && labs.isEnabled('dependencies') && (
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, hasDependencies: !prev.hasDependencies }))}
                      className="w-full flex items-start gap-3 px-2 py-1.5 text-sm text-left rounded hover:bg-surface-hover"
                    >
                      <div
                        className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${
                          filters.hasDependencies ? 'bg-blue-600 border-blue-600' : 'border-border-default'
                        }`}
                      >
                        {filters.hasDependencies && <CheckIcon className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="font-medium">Has {fc.label('dependencies')}</span>
                        <p className="text-xs text-text-tertiary">Show only initiatives with dependencies</p>
                      </div>
                    </button>
                  )}

                  <button
                    onClick={() => setFilters(prev => ({ ...prev, isStarred: !prev.isStarred }))}
                    className="w-full flex items-center gap-3 px-2 py-1.5 text-sm text-left rounded hover:bg-surface-hover"
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        filters.isStarred ? 'bg-blue-600 border-blue-600' : 'border-border-default'
                      }`}
                    >
                      {filters.isStarred && <CheckIcon className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <StarSolid className="w-3.5 h-3.5 text-amber-400" />
                      <span className="font-medium">Starred only</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Export Dropdown — only supports the canvas-based Timeline/List views */}
        {!hideExport && (viewType === 'timeline' || viewType === 'list') && (
        <ExportDropdown
          viewType={viewType}
          viewContainerRef={viewContainerRef}
          filterSummary={filterSummary}
          initiativeCount={customCount !== undefined ? customCount : filteredInitiatives.length}
          filteredInitiatives={filteredInitiatives}
        />
        )}

        {/* Saved View Heart Button — plain heart when the Saved Views lab is off */}
        {savedViewKey && !savedViewsOn && (
          <div className="relative">
            <button
              onClick={handleHeartClick}
              title={heartTitle}
              className={`p-1.5 rounded-md transition-colors ${
                heartActive
                  ? 'text-red-500 hover:text-red-600'
                  : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-hover'
              }`}
            >
              {heartActive
                ? <HeartSolid className="w-5 h-5" />
                : <HeartIcon className="w-5 h-5" />
              }
            </button>
            {heartTooltip && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-xs bg-gray-800 text-white rounded whitespace-nowrap z-50 pointer-events-none">
                {heartTooltip}
              </div>
            )}
          </div>
        )}

        {/* Save / Favorite / Manage View — behavior depends on role and context */}
        {savedViewsOn && (
          <div className="relative" ref={viewMenuRef}>
            {/* Single trigger → opens the action menu in every context */}
            <button
              onClick={() => setViewMenuOpen((o) => !o)}
              title={activeView ? `View actions for "${activeView.title}"` : 'View actions'}
              className={`p-1.5 rounded-md transition-colors flex items-center gap-0.5 ${
                activeView || heartActive
                  ? 'text-blue-600 hover:bg-surface-hover'
                  : 'text-text-secondary hover:text-blue-600 hover:bg-surface-hover'
              }`}
            >
              {activeView || heartActive ? <BookmarkSolid className="w-4 h-4" /> : <BookmarkIcon className="w-4 h-4" />}
              <ChevronDownIcon className={`w-3 h-3 transition-transform ${viewMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {saveTooltip && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-1 text-xs bg-gray-900 text-white rounded z-50 whitespace-nowrap">
                {saveTooltip}
              </div>
            )}

            {viewMenuOpen && activeView && (
              /* Inside a saved view — all actions shown; those you can't perform are greyed */
              <div className="absolute top-full right-0 mt-1 w-56 bg-background-elevated border border-border-default rounded-md shadow-lg z-50 py-1">
                <button
                  onClick={handleToggleFavorite}
                  title={activeView.isFavorite ? 'This view auto-loads for you' : 'Make this view auto-load for you'}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover"
                >
                  {activeView.isFavorite
                    ? <HeartSolid className="w-4 h-4 flex-shrink-0 text-red-500" />
                    : <HeartIcon className="w-4 h-4 flex-shrink-0" />}
                  Favorite View
                </button>

                <div className="my-1 border-t border-border-subtle" />

                {activeView.canManage ? (
                  <button
                    onClick={handleUpdateView}
                    disabled={!isViewDirty}
                    title={isViewDirty ? 'Save current filter changes to this view' : 'No filter changes to save'}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm ${
                      isViewDirty ? 'text-text-primary hover:bg-surface-hover' : 'text-gray-300 cursor-default'
                    }`}
                  >
                    <ArrowPathIcon className="w-4 h-4 flex-shrink-0" />
                    Update
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default" title="Only the view owner or an Admin can update this view">
                    <ArrowPathIcon className="w-4 h-4 flex-shrink-0" />
                    Update
                  </div>
                )}

                {activeView.canManage ? (
                  <button
                    onClick={openEditModal}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover"
                  >
                    <PencilIcon className="w-4 h-4 flex-shrink-0" />
                    Edit
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default" title="Only the view owner or an Admin can edit this view">
                    <PencilIcon className="w-4 h-4 flex-shrink-0" />
                    Edit
                  </div>
                )}

                {canCreateViews ? (
                  <button
                    onClick={() => openSaveModal('saveas')}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 flex-shrink-0" />
                    Save As..
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default" title="Only Editors and Admins can create a new view">
                    <DocumentDuplicateIcon className="w-4 h-4 flex-shrink-0" />
                    Save As..
                  </div>
                )}

                {activeView.canManage ? (
                  <button
                    onClick={handleDeleteView}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-surface-hover"
                  >
                    <TrashIcon className="w-4 h-4 flex-shrink-0" />
                    Delete
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default" title="Only the view owner or an Admin can delete this view">
                    <TrashIcon className="w-4 h-4 flex-shrink-0" />
                    Delete
                  </div>
                )}
              </div>
            )}

            {viewMenuOpen && !activeView && (
              /* Default view — only Favorite (and Save As.. for Editors/Admins on a named surface) are applicable */
              <div className="absolute top-full right-0 mt-1 w-56 bg-background-elevated border border-border-default rounded-md shadow-lg z-50 py-1">
                <button
                  onClick={hasActiveFilters ? () => { setViewMenuOpen(false); handleHeartClick(); } : undefined}
                  disabled={!hasActiveFilters}
                  title={
                    !hasActiveFilters
                      ? 'Apply filters first to save your default'
                      : heartActive
                        ? 'Clear my default filters (back to the standard view)'
                        : 'Save these filters as my default view'
                  }
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm ${
                    hasActiveFilters ? 'text-text-primary hover:bg-surface-hover' : 'text-gray-300 cursor-default'
                  }`}
                >
                  {heartActive
                    ? <HeartSolid className="w-4 h-4 flex-shrink-0 text-red-500" />
                    : <HeartIcon className="w-4 h-4 flex-shrink-0" />}
                  Favorite
                </button>

                <div className="my-1 border-t border-border-subtle" />

                {/* Not applicable on the Default view — shown greyed for clarity */}
                <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default">
                  <ArrowPathIcon className="w-4 h-4 flex-shrink-0" />
                  Update
                </div>
                <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default">
                  <PencilIcon className="w-4 h-4 flex-shrink-0" />
                  Edit
                </div>
                {canCreateViews && isNamedSurface ? (
                  <button
                    onClick={hasActiveFilters || pendingExtraFilterState ? () => openSaveModal('create') : undefined}
                    disabled={!hasActiveFilters && !pendingExtraFilterState}
                    title={
                      hasActiveFilters || pendingExtraFilterState
                        ? 'Create a new named, shareable view from these filters'
                        : 'Apply filters first to save a new view'
                    }
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm ${
                      hasActiveFilters || pendingExtraFilterState ? 'text-text-primary hover:bg-surface-hover' : 'text-gray-300 cursor-default'
                    }`}
                  >
                    <DocumentDuplicateIcon className="w-4 h-4 flex-shrink-0" />
                    Save As..
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default">
                    <DocumentDuplicateIcon className="w-4 h-4 flex-shrink-0" />
                    Save As..
                  </div>
                )}
                <div className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-300 cursor-default">
                  <TrashIcon className="w-4 h-4 flex-shrink-0" />
                  Delete
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unsaved change that can't be saved onto the default view (e.g. a Kanban column
            edit) — hint rather than force a save prompt; the change stays local until the
            user opens the menu and saves it, and is lost on navigating away otherwise. */}
        {savedViewsOn && pendingExtraFilterState && !activeView && (
          <button
            onClick={() => setViewMenuOpen(true)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap flex-shrink-0"
            title="Unsaved changes — open the menu to save them as a new view"
          >
            Save As…
          </button>
        )}
      </div>

      {/* Right side: Active chips, Count and Clear/Reset */}
      <div className="flex items-center gap-3 text-sm flex-shrink-0">
        {filters.isStarred && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
            <StarSolid className="w-3 h-3" />
            Starred
            <button
              type="button"
              onClick={() => setFilters(prev => ({ ...prev, isStarred: false }))}
              className="ml-0.5 hover:text-amber-900"
              aria-label="Remove starred filter"
            >
              ×
            </button>
          </span>
        )}
        <span className="flex flex-col items-end leading-tight text-text-secondary whitespace-nowrap">
          <span>
            {isMedium ? (
              <>
                {customCountLabel || 'Found'}: <span className="font-medium text-text-primary">{customCount !== undefined ? customCount : filteredInitiatives.length}</span>
              </>
            ) : (
              <>
                <span className="font-medium text-text-primary">{customCount !== undefined ? customCount : filteredInitiatives.length}</span> found
              </>
            )}
          </span>
          {viewMode === 'weekly' && weeklyDatedCount !== undefined && weeklyDatedCount > 0 && (
            <span className="text-xs text-text-tertiary italic">{weeklyDatedCount} with Dates</span>
          )}
        </span>
        {deviatedFromViewDefault && (
          <button
            onClick={resetToViewDefault}
            className="text-blue-600 hover:text-blue-700 hover:underline"
          >
            {activeView || (savedState ?? (lsKey ? readLsState(lsKey) : null))
              ? 'Reset'
              : 'Clear filters'
            }
          </button>
        )}
      </div>
    </div>

    {namedViewsOn && canCreateViews && (
      <SaveViewModal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        viewType={viewType}
        filterState={saveMode === 'edit' && activeView ? activeView.filterState : currentFilterState()}
        existingView={saveMode === 'edit' ? activeView : undefined}
        onSaved={handleViewSaved}
      />
    )}
    </>
  );
}
